import { useState } from "react";
import {
  ArrowRight,
  Check,
  ChevronDown,
  ClipboardCheck,
  Copy,
  ExternalLink,
  FileCheck2,
  Flag,
  RotateCcw,
  ShieldCheck,
} from "lucide-react";
import { LONGHAND_GUIDE_SCREENSHOTS } from "@/data/longhandAIProgramGuide";
import {
  LONGHAND_OPTIONAL_BUILD_SETUP_STEPS,
  LONGHAND_WORKSPACE_SETUP_STEPS,
  type WorkspaceSetupStep,
} from "@/data/longhandWorkspaceSetup";

const copyText = async (value: string) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch {
      // Permission-restricted browsers may expose clipboard without allowing writes.
    }
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
};

const SetupScreenshots = ({ screenshotIds }: { screenshotIds: string[] }) => (
  <div className={`lhp-screenshot-grid ${screenshotIds.length > 1 ? "is-paired" : ""}`}>
    {screenshotIds.map((screenshotId) => {
      const screenshot = LONGHAND_GUIDE_SCREENSHOTS[screenshotId];
      if (!screenshot) return null;

      return (
        <figure key={screenshot.id} className="lhp-screenshot">
          <a href={screenshot.src} target="_blank" rel="noopener noreferrer" aria-label={`Open full-size screenshot: ${screenshot.alt}`}>
            <img
              src={screenshot.src}
              width={screenshot.width}
              height={screenshot.height}
              alt={screenshot.alt}
              loading="lazy"
              decoding="async"
            />
          </a>
          <figcaption>{screenshot.caption} <span>Open the image to inspect the full-size capture.</span></figcaption>
        </figure>
      );
    })}
  </div>
);

const SetupPrompt = ({
  step,
  copiedId,
  onCopy,
}: {
  step: WorkspaceSetupStep;
  copiedId: string | null;
  onCopy: (id: string, value: string) => void;
}) => {
  if (!step.prompt) return null;

  const copyId = `workspace-prompt-${step.id}`;
  const copied = copiedId === copyId;

  return (
    <section className="lhp-prompt lhp-lab-prompt" aria-labelledby={`${copyId}-title`}>
      <div className="lhp-prompt-head">
        <div>
          <p>Copy and customize</p>
          <h4 id={`${copyId}-title`}>{step.prompt.label}</h4>
        </div>
        <button type="button" onClick={() => onCopy(copyId, step.prompt!.value)} aria-live="polite">
          {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
          {copied ? "Copied" : "Copy prompt"}
        </button>
      </div>
      <pre><code>{step.prompt.value}</code></pre>
    </section>
  );
};

const WorkspaceSetupCard = ({
  step,
  number,
  copiedId,
  onCopy,
}: {
  step: WorkspaceSetupStep;
  number: number;
  copiedId: string | null;
  onCopy: (id: string, value: string) => void;
}) => (
  <article className="lhp-setup-step" id={step.id}>
    <header className="lhp-setup-step-head">
      <span>{String(number).padStart(2, "0")}</span>
      <div>
        <p>{step.kicker}</p>
        <h3>{step.title}</h3>
        <small>{step.instruction}</small>
      </div>
    </header>

    {step.screenshotIds && <SetupScreenshots screenshotIds={step.screenshotIds} />}

    <div className="lhp-setup-platforms">
      {step.paths.map((path) => (
        <section className="lhp-setup-platform" data-platform={path.platform} key={path.platform} aria-labelledby={`${step.id}-${path.platform}`}>
          <header>
            <span>{path.platform === "claude" ? "C" : "G"}</span>
            <h4 id={`${step.id}-${path.platform}`}>{path.label}</h4>
          </header>

          <SetupScreenshots screenshotIds={path.screenshotIds} />

          <p className="lhp-setup-label">Do this</p>
          <ol>
            {path.actions.map((action, index) => (
              <li key={action}><span>{index + 1}</span><p>{action}</p></li>
            ))}
          </ol>

          <div className="lhp-setup-links">
            {path.links.map((link) => {
              const external = /^https?:\/\//.test(link.href);
              return (
                <a
                  key={link.href}
                  href={link.href}
                  download={link.download}
                  target={external ? "_blank" : undefined}
                  rel={external ? "noopener noreferrer" : undefined}
                >
                  {link.label}
                  {external ? <ExternalLink aria-hidden="true" /> : <ArrowRight aria-hidden="true" />}
                </a>
              );
            })}
          </div>

          {path.note && <p className="lhp-setup-note"><strong>Current interface note</strong>{path.note}</p>}
        </section>
      ))}
    </div>

    <SetupPrompt step={step} copiedId={copiedId} onCopy={onCopy} />

    <div className="lhp-result-grid lhp-setup-result">
      <section className="lhp-expected" aria-labelledby={`${step.id}-expected`}>
        <FileCheck2 aria-hidden="true" />
        <div>
          <h4 id={`${step.id}-expected`}>What you should see</h4>
          <p>{step.expected}</p>
        </div>
      </section>
      <section className="lhp-verify" aria-labelledby={`${step.id}-verify`}>
        <ClipboardCheck aria-hidden="true" />
        <div>
          <h4 id={`${step.id}-verify`}>Check before moving on</h4>
          <ul>{step.verify.map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
      </section>
    </div>

    <p className="lhp-step-stop lhp-setup-stop"><Flag aria-hidden="true" /><span><strong>Human stop rule:</strong> {step.stop}</span></p>

    <details className="lhp-recovery lhp-setup-recovery">
      <summary><span><RotateCcw aria-hidden="true" /> If this step fails</span><ChevronDown aria-hidden="true" /></summary>
      <ul>{step.recover.map((item) => <li key={item}>{item}</li>)}</ul>
    </details>
  </article>
);

const LonghandWorkspaceSetup = () => {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = async (id: string, value: string) => {
    await copyText(value);
    setCopiedId(id);
    window.setTimeout(() => setCopiedId((current) => current === id ? null : current), 1800);
  };

  return (
    <section className="lhp-prepare" id="prepare" aria-labelledby="prepare-title">
      <div className="lhp-prepare-head">
        <p className="lhp-eyebrow">Step 00</p>
        <h1 id="prepare-title">Prepare your workspace.</h1>
        <p>Choose Claude or ChatGPT, then follow that path through all five setup steps. You will finish with one reusable workflow and one bounded Chief of Staff test. If you plan to build and share a working project, continue with the optional GitHub and Vercel steps at the end.</p>
      </div>

      <div className="lhp-setup-steps">
        {LONGHAND_WORKSPACE_SETUP_STEPS.map((step, index) => (
          <WorkspaceSetupCard
            key={step.id}
            step={step}
            number={index + 1}
            copiedId={copiedId}
            onCopy={handleCopy}
          />
        ))}
      </div>

      <details className="lhp-optional-setup" id="optional-build-setup">
        <summary>
          <div>
            <p>Optional build setup</p>
            <h2>Put a project on GitHub and Vercel.</h2>
            <small>Continue here when you want Claude Code or Codex to work on a reviewable project and create a shareable preview.</small>
          </div>
          <ChevronDown aria-hidden="true" />
        </summary>
        <div className="lhp-setup-steps">
          {LONGHAND_OPTIONAL_BUILD_SETUP_STEPS.map((step, index) => (
            <WorkspaceSetupCard
              key={step.id}
              step={step}
              number={LONGHAND_WORKSPACE_SETUP_STEPS.length + index + 1}
              copiedId={copiedId}
              onCopy={handleCopy}
            />
          ))}
        </div>
      </details>

      <aside><ShieldCheck aria-hidden="true" /><p><strong>Current interface check.</strong> Product names, menus, plans, and permission models change. Use the live controls in your approved account, and pause when the current screen asks for more access than this guide describes.</p></aside>
    </section>
  );
};

export default LonghandWorkspaceSetup;
